import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createEvent } from '../../api/api';
import { MentionTextarea } from '../ui/mention-textarea';
import { createMentionFetcher } from '../../lib/mentions';
import SignedOutPrompt from '../SignedOutPrompt';
import { isAccountVerified } from '../utils';
import ImagePreviewStrip from './ImagePreviewStrip';
import {
    appendImageFiles,
    uploadImageFile,
} from '../../lib/contentImages';
import {
    blockLocationAutofillProps,
    isUserInputEvent,
    revertUntrustedInput,
} from '../../lib/blockBrowserAutofill';

/**
 * Create an event as the logged-in user or organization account.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
const CreateEvent = ({ userId, user }) => {
    const navigate = useNavigate();
    const mentionRef = useRef(null);
    const fileInputRef = useRef(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [locationReady, setLocationReady] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [imageItems, setImageItems] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const fetchCandidates = useMemo(
        () => createMentionFetcher(userId),
        [userId]
    );

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files || []);
        e.target.value = '';
        if (!selected.length) return;
        const { items, error: addError } = appendImageFiles(selected, imageItems);
        setImageItems(items);
        setError(addError);
    };

    const removeImage = (id) => {
        setImageItems((prev) => {
            const found = prev.find((item) => item.id === id);
            if (found) URL.revokeObjectURL(found.preview);
            return prev.filter((item) => item.id !== id);
        });
    };

    const reorderImages = (fromId, toId) => {
        setImageItems((prev) => {
            const from = prev.findIndex((item) => item.id === fromId);
            const to = prev.findIndex((item) => item.id === toId);
            if (from < 0 || to < 0 || from === to) return prev;
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId) {
            setError('You must be logged in to create an event');
            return;
        }
        if (user && !isAccountVerified(user)) {
            setError('Your account must be verified before you can create events.');
            return;
        }
        if (!title.trim() || !startTime || !endTime) {
            setError('Title, start time, and end time are required');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const images = [];
            for (const item of imageItems) {
                images.push(await uploadImageFile(item.file));
            }

            const result = await createEvent({
                author_id: userId,
                author_is_org: user?.type === 'organization',
                title: title.trim(),
                description: (mentionRef.current?.getSerializedValue?.() ?? description).trim(),
                location: location.trim(),
                start_time: new Date(startTime).toISOString(),
                end_time: new Date(endTime).toISOString(),
                images,
            });

            if (result.error || result.success === false) {
                setError(result.error || result.message || 'Failed to create event');
            } else {
                navigate('/feed');
            }
        } catch (err) {
            setError(err.message || 'Failed to create event');
        } finally {
            setSubmitting(false);
        }
    };

    if (!userId) {
        return (
            <SignedOutPrompt
                title="Create an event"
                description="Sign in to create events for your community and share them with your network."
            />
        );
    }

    if (user && !isAccountVerified(user)) {
        return (
            <SignedOutPrompt
                variant="restricted"
                title="Create an event"
                description="Your account must be verified before you can create events. Until then, you can browse Explore and manage your profile."
            />
        );
    }

    return (
        <div className="max-w-xl mx-auto mt-10 p-8 bg-secondary rounded-xl shadow-lg">
            <h1 className="text-3xl font-bold text-text-primary mb-4">Create New Event</h1>
            <p className="text-base text-text-secondary mb-6">
                Fill out the details below to create a new event for your community. Make sure to provide all the necessary information so attendees know what to expect.
            </p>
            {error && (
                <p className="mb-4 text-sm text-error">{error}</p>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                        Event Images
                    </label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                    />
                    <ImagePreviewStrip
                        items={imageItems}
                        onReorder={reorderImages}
                        onRemove={removeImage}
                        onAdd={() => fileInputRef.current?.click()}
                        disabled={submitting}
                        showAddWhenEmpty
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                        Title
                    </label>
                    <input
                        type="text"
                        className="w-full border border-border rounded px-3 py-2 bg-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="Event Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                        Description
                    </label>
                    <MentionTextarea
                        expanding={false}
                        ref={mentionRef}
                        className="bg-primary"
                        rows={4}
                        placeholder="Event Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fetchCandidates={fetchCandidates}
                        excludeId={userId}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                        Location
                    </label>
                    <input
                        type="text"
                        name="place-query"
                        className="w-full border border-border rounded px-3 py-2 bg-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="Event Location"
                        value={location}
                        readOnly={!locationReady}
                        {...blockLocationAutofillProps}
                        onFocus={(event) => {
                            event.currentTarget.readOnly = false;
                            event.currentTarget.setAttribute("autocomplete", "new-password");
                            setLocationReady(true);
                        }}
                        onChange={(event) => {
                            if (!isUserInputEvent(event)) {
                                revertUntrustedInput(event, location);
                                return;
                            }
                            setLocation(event.target.value);
                        }}
                    />
                </div>
                <div className="flex space-x-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            Start Time
                        </label>
                        <input
                            type="datetime-local"
                            className="w-full border border-border rounded px-3 py-2 bg-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            required
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                            End Time
                        </label>
                        <input
                            type="datetime-local"
                            className="w-full border border-border rounded px-3 py-2 bg-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            required
                        />
                    </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                    <button
                        type="button"
                        className="bg-secondary text-error border border-error px-5 py-2 rounded font-semibold hover:bg-secondary"
                        onClick={() => navigate(-1)}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="bg-secondary text-accent border border-accent px-5 py-2 rounded font-semibold hover:bg-secondary disabled:opacity-50"
                        disabled={submitting}
                    >
                        {submitting ? 'Posting…' : 'Post Event'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateEvent;
